/**
 * PartnerWordenPage — IMPERIA AI
 *
 * Volwaardige website-stijl pagina (gebruikt WebLayout = Header + Footer).
 * Light theme, SEO-vriendelijk, met hero + benefits + 2 partner-keuzes:
 *   - Affiliate Marketing (online: link delen, content, social)
 *   - Salespartner (B2B: outbound, persoonlijk, eigen netwerk)
 *
 * Submissions worden:
 *  1. opgeslagen in DB (partner_applications, status='pending')
 *  2. gemaild naar info@imperiaai.nl als notificatie
 *
 * Aanmelding wordt pas actief NA admin approval.
 */
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { SEOHead } from '../components/SEOHead';
import { toast } from 'sonner';
import {
  Sparkles, Briefcase, ArrowRight, CheckCircle2, Send, Loader2,
  TrendingUp, Users, Award, Mail, Shield, Clock, ChevronRight, Handshake,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PARTNER_KINDS = [
  {
    value: 'affiliate',
    label: 'Affiliate Marketing',
    icon: Sparkles,
    tagline: 'Online · Performance based',
    summary: 'Verdien commissie via uw eigen unieke promo-link.',
    description: 'Voor influencers, content-makers, bloggers, social media accounts en website-eigenaren. U deelt uw unieke promo-link of -code en ontvangt commissie voor iedere klant die via u converteert. Geen vaste verplichtingen, maandelijks uitgekeerd.',
    perks: [
      '20% commissie op de eerste maand van iedere nieuwe klant',
      'Eigen promo-link + tracking dashboard',
      'Maandelijkse uitbetaling vanaf €100',
      'Marketing-materialen (banners, social posts, video\'s)',
    ],
    ideal: 'Online creators, bloggers, social media kanalen, website-eigenaren',
  },
  {
    value: 'salespartner',
    label: 'Salespartner',
    icon: Briefcase,
    tagline: 'B2B · Direct contact',
    summary: 'Verkoop IMPERIA AI aan MKB & zzp uit uw eigen netwerk.',
    description: 'Voor sales professionals, consultants, callcenters, marketing-adviseurs en field sales agents. U brengt warme leads aan en wij sluiten af. Hoge commissie per geactiveerde klant, plus terugkerende inkomsten op recurring revenue.',
    perks: [
      '€495,95 commissie per geactiveerde klant (eenmalig)',
      '10% terugkerende commissie zolang klant actief blijft',
      'Eigen account manager + verkoop-materialen',
      'Lead-CRM toegang en pipeline tracking',
    ],
    ideal: 'B2B sales, consultants, callcenters, marketing-adviseurs',
  },
];

const SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Partner worden bij IMPERIA AI',
  url: 'https://www.imperiaai.nl/partner-worden',
  description: 'Word affiliate of salespartner van IMPERIA AI. Verdien commissie door MKB-klanten aan te brengen aan ons AI-marketing platform.',
  mainEntity: {
    '@type': 'Service',
    serviceType: 'Affiliate & Reseller Partner Programma',
    provider: { '@type': 'Organization', name: 'IMPERIA AI' },
    offers: [
      { '@type': 'Offer', name: 'Affiliate Programma', description: '20% commissie eerste maand per klant' },
      { '@type': 'Offer', name: 'Salespartner Programma', description: '€495,95 per klant + recurring 10%' },
    ],
  },
};


export default function PartnerWordenPage() {
  const [kind, setKind] = useState('affiliate');
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    company_name: '',
    kvk: '',
    website: '',
    region: '',
    audience_size: '',
    experience: '',
    motivation: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef(null);

  const upd = (k, v) => setForm({ ...form, [k]: v });
  const selected = PARTNER_KINDS.find(k => k.value === kind);

  const scrollToForm = (e) => {
    e?.preventDefault();
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error('Vul minimaal uw naam en e-mailadres in.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/public/partner-apply`, {
        ...form,
        kind,
      });
      if (res.data?.already_applied) {
        toast.info(`U heeft al een aanmelding ingediend (status: ${res.data.status}).`);
      } else {
        toast.success('Aanmelding ontvangen — wij beoordelen deze binnen 5 werkdagen.');
      }
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verzenden mislukt. Probeer opnieuw of mail direct naar info@imperiaai.nl.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <>
        <SEOHead
          title="Aanmelding ontvangen — Partner worden bij IMPERIA AI"
          description="Uw partner-aanmelding bij IMPERIA AI is succesvol ontvangen."
          canonical="/partner-worden"
        />
        <section style={{ background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 60%, #0f172a 100%)', padding: '140px 0 120px', minHeight: '70vh' }}>
          <div className="container-custom max-w-3xl mx-auto px-4 text-center">
            <div style={{ width: 80, height: 80, margin: '0 auto 24px', borderRadius: '50%', background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle2 size={44} color="white" strokeWidth={2.5} />
            </div>
            <h1 data-testid="partner-success" style={{ fontFamily: "'Sora', sans-serif", fontSize: 40, fontWeight: 800, marginBottom: 16, color: 'white', lineHeight: 1.1 }}>
              Aanmelding ontvangen
            </h1>
            <p className="text-white/75 text-lg max-w-2xl mx-auto leading-relaxed mb-8">
              We hebben uw aanmelding als <strong style={{ color: '#D4A017' }}>{selected?.label}</strong> ontvangen.
              Een kopie is ook verstuurd naar <strong>info@imperiaai.nl</strong>.
              Binnen <strong>5 werkdagen</strong> nemen wij contact met u op met uw inloggegevens en een uitnodiging voor het intake-gesprek.
            </p>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 max-w-lg mx-auto text-left text-white/85">
              <p className="font-bold text-[#D4A017] mb-3">Wat gebeurt er na goedkeuring?</p>
              <ul className="space-y-2 text-sm">
                <li>✓ U krijgt uw login + tijdelijk wachtwoord per e-mail</li>
                <li>✓ Een directe link naar uw intake-gesprek (Calendly)</li>
                <li>✓ Uw persoonlijke promo-link of verkoop-materialen</li>
                <li>✓ Toegang tot het partner-dashboard met realtime tracking</li>
              </ul>
            </div>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/" className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-6 py-3 rounded-xl transition-colors">
                Terug naar website <ArrowRight size={16} />
              </Link>
              <Link to="/diensten" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white font-semibold px-6 py-3 rounded-xl transition-colors">
                Bekijk onze diensten <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title="Partner worden — Affiliate of Salespartner van IMPERIA AI"
        description="Word affiliate of salespartner van IMPERIA AI. Verdien tot €495,95 per nieuwe MKB-klant + recurring commissie. Aanmelden voor het partnerprogramma in 2 minuten."
        canonical="/partner-worden"
        keywords="partner worden imperia ai, affiliate marketing nederland, salespartner mkb, reseller programma ai marketing, partner programma mkb nederland, samenwerken marketing platform"
        schema={SCHEMA}
      />

      {/* ─── HERO ────────────────────────────────────────────────── */}
      <section
        style={{
          background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 55%, #0f172a 100%)',
          padding: '120px 0 70px',
        }}
        data-testid="partner-hero"
      >
        <div className="container-custom max-w-5xl mx-auto px-4 text-center">
          <span className="inline-flex items-center gap-2 bg-[#D4A017]/15 border border-[#D4A017]/30 rounded-full px-3 py-1.5 mb-6">
            <Handshake size={12} className="text-[#D4A017]" />
            <span className="text-[#D4A017] text-xs font-bold tracking-wide uppercase">Partnerprogramma · Nederland</span>
          </span>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] mb-6"
            style={{ fontFamily: "'Sora', sans-serif" }}>
            Word <span style={{ color: '#D4A017' }}>affiliate</span> of <span style={{ color: '#D4A017' }}>salespartner</span> van IMPERIA AI
          </h1>
          <p className="text-white/75 text-base md:text-lg max-w-2xl mx-auto leading-relaxed mb-8">
            Verdien commissie door MKB-bedrijven en zzp-ers in contact te brengen met ons AI-marketing platform.
            U brengt aan — wij verzorgen platform, fulfilment en facturatie. Twee duidelijke routes, één partnerprogramma.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="#partner-form"
              onClick={scrollToForm}
              data-testid="hero-cta-form"
              className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-6 py-3 rounded-xl transition-colors"
            >
              Direct aanmelden <ArrowRight size={16} />
            </a>
            <a
              href="#partner-keuzes"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
            >
              Bekijk de programma&apos;s <ChevronRight size={16} />
            </a>
          </div>

          {/* Trust strip */}
          <div className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-3 text-white/60 text-sm">
            <span className="inline-flex items-center gap-2"><Award size={14} className="text-[#D4A017]" /> 200+ actieve partners</span>
            <span className="inline-flex items-center gap-2"><Clock size={14} className="text-[#D4A017]" /> Aanmelding binnen 5 werkdagen verwerkt</span>
            <span className="inline-flex items-center gap-2"><Shield size={14} className="text-[#D4A017]" /> Maandelijkse uitbetaling</span>
          </div>
        </div>
      </section>

      {/* ─── BENEFITS BAR ─────────────────────────────────────────── */}
      <section style={{ background: 'white', padding: '50px 0', borderBottom: '1px solid #e2e8f0' }}>
        <div className="container-custom max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <BenefitStat icon={TrendingUp} value="20%" label="Commissie 1e maand (affiliate)" />
            <BenefitStat icon={Briefcase} value="€495,95" label="Per klant (salespartner)" />
            <BenefitStat icon={Users} value="10%" label="Recurring revenue share" />
            <BenefitStat icon={Mail} value="24u" label="Reactie op uw vragen" />
          </div>
        </div>
      </section>

      {/* ─── KEUZES (Affiliate vs Salespartner) ───────────────────── */}
      <section id="partner-keuzes" style={{ background: '#f8fafc', padding: '80px 0' }}>
        <div className="container-custom max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <span style={{ color: '#D4A017', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Twee routes · één programma</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0f172a] mt-3 mb-3" style={{ fontFamily: "'Sora', sans-serif" }}>
              Welk type partner past bij u?
            </h2>
            <p className="text-[#64748b] max-w-2xl mx-auto">
              Kies de route die het beste past bij hoe u werkt. U kunt op elk moment overstappen of beide combineren.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {PARTNER_KINDS.map(k => {
              const Icon = k.icon;
              const active = kind === k.value;
              return (
                <button
                  key={k.value}
                  type="button"
                  onClick={() => { setKind(k.value); scrollToForm(); }}
                  data-testid={`partner-kind-${k.value}`}
                  className={`text-left bg-white rounded-2xl p-7 border-2 transition-all hover:shadow-lg ${active ? 'border-[#D4A017] shadow-lg' : 'border-slate-200'}`}
                  style={{ display: 'block', width: '100%' }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: active ? '#D4A017' : '#0f172a' }}>
                      <Icon size={22} color={active ? '#0f172a' : '#D4A017'} />
                    </div>
                    {active && (
                      <span className="bg-[#D4A017] text-[#0f172a] text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded">Geselecteerd</span>
                    )}
                  </div>
                  <span style={{ color: active ? '#D4A017' : '#94a3b8', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    {k.tagline}
                  </span>
                  <h3 className="text-xl font-bold text-[#0f172a] mt-2 mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>{k.label}</h3>
                  <p className="text-[#64748b] text-sm mb-4 leading-relaxed">{k.description}</p>
                  <ul className="space-y-2 mb-4">
                    {k.perks.map(p => (
                      <li key={p} className="flex items-start gap-2 text-sm text-[#374151]">
                        <CheckCircle2 size={16} className="text-[#10b981] mt-0.5 flex-shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-[#94a3b8] border-t border-slate-100 pt-3">
                    <strong>Ideaal voor:</strong> {k.ideal}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── FORM ────────────────────────────────────────────────── */}
      <section id="partner-form" ref={formRef} style={{ background: 'white', padding: '80px 0' }}>
        <div className="container-custom max-w-3xl mx-auto px-4">
          <div className="text-center mb-10">
            <span style={{ color: '#D4A017', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Aanmelden · 2 minuten</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0f172a] mt-3 mb-3" style={{ fontFamily: "'Sora', sans-serif" }}>
              Aanmelden als <span style={{ color: '#D4A017' }}>{selected?.label}</span>
            </h2>
            <p className="text-[#64748b]">
              Vul uw gegevens in. We beoordelen elke aanmelding handmatig en bevestigen binnen 5 werkdagen per e-mail.
            </p>
          </div>

          <form onSubmit={submit} className="bg-slate-50 border border-slate-200 rounded-2xl p-7" data-testid="partner-form">
            {/* Type switcher binnen form (compact) */}
            <div className="mb-6">
              <label className="block text-xs font-bold uppercase tracking-wider text-[#64748b] mb-2">Partnertype</label>
              <div className="grid grid-cols-2 gap-2">
                {PARTNER_KINDS.map(k => (
                  <button
                    key={k.value}
                    type="button"
                    onClick={() => setKind(k.value)}
                    data-testid={`form-kind-${k.value}`}
                    className={`py-3 px-4 rounded-xl text-sm font-semibold transition-all ${kind === k.value ? 'bg-[#0f172a] text-white' : 'bg-white border border-slate-300 text-[#374151] hover:border-[#D4A017]'}`}
                  >
                    {k.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Volledige naam *" value={form.full_name} onChange={(v) => upd('full_name', v)} testId="partner-full-name" required />
              <Field label="E-mailadres *" type="email" value={form.email} onChange={(v) => upd('email', v)} testId="partner-email" required />
              <Field label="Telefoon" value={form.phone} onChange={(v) => upd('phone', v)} testId="partner-phone" />
              <Field label="Bedrijfsnaam" value={form.company_name} onChange={(v) => upd('company_name', v)} testId="partner-company" />
              <Field label="KvK-nummer" value={form.kvk} onChange={(v) => upd('kvk', v)} testId="partner-kvk" />
              <Field label="Website / kanaal" value={form.website} onChange={(v) => upd('website', v)} testId="partner-website" placeholder="https://..." />
              <Field
                label={kind === 'affiliate' ? 'Bereik (volgers/bezoekers)' : 'Regio / werkgebied'}
                value={kind === 'affiliate' ? form.audience_size : form.region}
                onChange={(v) => upd(kind === 'affiliate' ? 'audience_size' : 'region', v)}
                testId="partner-region-or-audience"
                placeholder={kind === 'affiliate' ? '5.000 volgers, 15k bezoekers/mnd' : 'Randstad, NL noord, Vlaanderen'}
              />
              <Field label="Ervaring (kort)" value={form.experience} onChange={(v) => upd('experience', v)} testId="partner-experience" placeholder="Waar / hoe heeft u eerder gewerkt?" />
            </div>

            <div className="mt-4">
              <Field
                label={kind === 'affiliate' ? 'Hoe wilt u IMPERIA AI promoten?' : 'Hoe wilt u klanten aanbrengen?'}
                value={form.motivation}
                onChange={(v) => upd('motivation', v)}
                testId="partner-motivation"
                textarea
                placeholder={kind === 'affiliate'
                  ? 'Bv. eigen blog, YouTube-kanaal, e-mail-lijst, social media…'
                  : 'Bv. bestaand klantnetwerk, cold-outreach, beurzen, persoonlijke gesprekken…'}
              />
            </div>

            <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
              <strong>Belangrijk:</strong> uw partner-account is <strong>nog niet</strong> actief. Wij beoordelen elke aanmelding handmatig
              en bevestigen binnen 5 werkdagen per e-mail. Een kopie van deze aanmelding wordt automatisch verstuurd naar <strong>info@imperiaai.nl</strong>.
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="partner-submit"
              className="w-full mt-5 bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold py-4 rounded-xl transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Versturen…
                </>
              ) : (
                <>
                  <Send size={16} /> Verstuur aanmelding
                  <ArrowRight size={16} />
                </>
              )}
            </button>

            <p className="text-center text-xs text-[#94a3b8] mt-3">
              Vragen vooraf? Mail <a href="mailto:info@imperiaai.nl" className="text-[#D4A017] hover:underline">info@imperiaai.nl</a> of bel <a href="tel:+31382340037" className="text-[#D4A017] hover:underline">038 234 00 37</a>.
            </p>
          </form>
        </div>
      </section>

      {/* ─── FAQ ─────────────────────────────────────────────────── */}
      <section style={{ background: '#f8fafc', padding: '70px 0' }}>
        <div className="container-custom max-w-3xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0f172a] mb-8 text-center" style={{ fontFamily: "'Sora', sans-serif" }}>
            Veelgestelde vragen
          </h2>
          <div className="space-y-3">
            <Faq q="Hoe snel weet ik of mijn aanmelding is goedgekeurd?">
              Binnen 5 werkdagen krijgt u per e-mail bericht. Bij goedkeuring sturen we direct uw inloggegevens en een Calendly-link voor het intake-gesprek.
            </Faq>
            <Faq q="Hoe en wanneer krijg ik uitbetaald?">
              Maandelijkse uitbetaling per SEPA bankoverschrijving, mits uw saldo €100 (affiliate) of €495,95 (salespartner) overschrijdt. Uitbetaling rond de 15e van iedere maand.
            </Faq>
            <Faq q="Kan ik beide programma's combineren?">
              Ja. Veel partners starten als affiliate (online via promo-link) en groeien door naar salespartner (persoonlijk netwerk). U mag beide rollen tegelijk hebben.
            </Faq>
            <Faq q="Wat als ik geen bedrijf heb (geen KvK)?">
              Voor het affiliate programma is een KvK niet verplicht — u kunt deelnemen als particuliere content-creator. Voor salespartner is een KvK wel vereist (B2B factuur).
            </Faq>
            <Faq q="Hoe weet u of een klant via mij komt?">
              Iedere partner krijgt een unieke promo-link of code. We tracken via cookie + UTM-parameters + (bij salespartner) handmatige toewijzing in het CRM. Alles is transparant zichtbaar in uw dashboard.
            </Faq>
          </div>
        </div>
      </section>

      {/* ─── CLOSING CTA ─────────────────────────────────────────── */}
      <section style={{ background: '#0f172a', padding: '70px 0' }}>
        <div className="container-custom max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-4" style={{ fontFamily: "'Sora', sans-serif" }}>
            Klaar om mee te verdienen aan IMPERIA AI?
          </h2>
          <p className="text-white/70 mb-6">Aanmelding kost 2 minuten. Goedkeuring binnen 5 werkdagen.</p>
          <a
            href="#partner-form"
            onClick={scrollToForm}
            className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-7 py-3.5 rounded-xl transition-colors"
          >
            Direct aanmelden <ArrowRight size={16} />
          </a>
        </div>
      </section>
    </>
  );
}


function BenefitStat({ icon: Icon, value, label }) {
  return (
    <div>
      <Icon size={22} className="text-[#D4A017] mx-auto mb-2" />
      <p className="text-2xl font-extrabold text-[#0f172a]" style={{ fontFamily: "'Sora', sans-serif" }}>{value}</p>
      <p className="text-xs text-[#64748b] mt-1">{label}</p>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', testId, textarea, placeholder, required }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-wider text-[#64748b] mb-1.5">{label}</span>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          placeholder={placeholder}
          rows={4}
          required={required}
          className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-[#0f172a] focus:outline-none focus:border-[#D4A017] focus:ring-1 focus:ring-[#D4A017] resize-y"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          placeholder={placeholder}
          required={required}
          className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-[#0f172a] focus:outline-none focus:border-[#D4A017] focus:ring-1 focus:ring-[#D4A017]"
        />
      )}
    </label>
  );
}

function Faq({ q, children }) {
  return (
    <details className="bg-white border border-slate-200 rounded-xl p-4 group">
      <summary className="font-semibold text-[#0f172a] cursor-pointer list-none flex justify-between items-center">
        <span>{q}</span>
        <ChevronRight size={16} className="text-[#94a3b8] group-open:rotate-90 transition-transform" />
      </summary>
      <p className="text-sm text-[#64748b] mt-3 leading-relaxed">{children}</p>
    </details>
  );
}
